(() => {
  if (window.RwadyCartShipping) return;

  const UPDATE_EVENT = 'maximize:cart:updated';
  const POLL_DELAYS = [0, 400, 700, 1000, 1400];
  let requestSequence = 0;
  const selectedPrices = new WeakMap();
  const reviewedPrices = new WeakMap();

  const wait = (delay) => new Promise((resolve) => window.setTimeout(resolve, delay));

  const formatIqd = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return null;
    return `${new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(Math.round(amount))} IQD`;
  };

  const getLowestRate = (rates) => {
    if (!Array.isArray(rates) || rates.length === 0) return null;

    return rates.reduce((lowest, rate) => {
      const price = Number(String(rate.price).replace(/,/g, ''));
      if (!Number.isFinite(price)) return lowest;
      if (!lowest || price < lowest.price) return { rate, price };
      return lowest;
    }, null);
  };

  const fetchPreparedRates = async (root, params, sequence) => {
    const prepareResponse = await fetch(`${root}cart/prepare_shipping_rates.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'Accept': 'application/json'
      },
      body: params.toString(),
      credentials: 'same-origin'
    });

    if (!prepareResponse.ok) throw new Error(`Shipping rate preparation failed: ${prepareResponse.status}`);

    for (const delay of POLL_DELAYS) {
      if (sequence !== requestSequence) return null;
      if (delay) await wait(delay);

      const response = await fetch(`${root}cart/async_shipping_rates.json?${params.toString()}`, {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      });

      if (response.status === 202) continue;
      if (!response.ok) throw new Error(`Shipping rate request failed: ${response.status}`);

      const result = await response.json();
      if (Array.isArray(result.shipping_rates)) return result.shipping_rates;
    }

    return [];
  };

  const update = async () => {
    const summaries = Array.from(document.querySelectorAll('[data-rwady-cart-shipping]'));
    if (!summaries.length) return;

    const primarySummary = summaries[0];
    const countryCode = primarySummary.dataset.countryCode;
    if (!countryCode) return;

    const sequence = ++requestSequence;
    summaries.forEach((summary) => {
      const shippingOutput = summary.querySelector('[data-rwady-shipping-price]');
      if (shippingOutput && !reviewedPrices.has(summary)) {
        shippingOutput.textContent = summary.dataset.shippingLoadingLabel || 'Calculating shipping…';
      }
    });

    try {
      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams();

      // Region and postal code are intentionally omitted: Shopify selects the
      // configured rate for the cart's total weight within the current market.
      params.set('shipping_address[country]', countryCode);

      const rates = await fetchPreparedRates(root, params, sequence);
      if (sequence !== requestSequence) return;

      const selected = getLowestRate(rates);
      summaries.forEach((summary) => {
        const shippingOutput = summary.querySelector('[data-rwady-shipping-price]');
        const totalOutput = summary.querySelector('[data-rwady-order-total]');
        const fallback = summary.dataset.shippingFallbackLabel || 'Calculated at checkout';
        const cartTotalCents = Number(summary.dataset.cartEffectiveTotalCents || summary.dataset.cartTotalCents);

        if (!shippingOutput || !totalOutput) return;
        if (!selected) {
          selectedPrices.delete(summary);
          delete summary.dataset.shippingPrice;
          shippingOutput.textContent = fallback;
          return;
        }

        shippingOutput.textContent = formatIqd(selected.price) || fallback;
        selectedPrices.set(summary, selected.price);
        summary.dataset.shippingPrice = String(selected.price);
        if (Number.isFinite(cartTotalCents)) {
          const total = (cartTotalCents / 100) + selected.price;
          const formattedTotal = formatIqd(total);
          if (formattedTotal) totalOutput.textContent = formattedTotal;
        }
      });
      summaries.forEach(applyReviewedPrice);
    } catch (error) {
      if (sequence !== requestSequence) return;
      summaries.forEach((summary) => {
        selectedPrices.delete(summary);
        delete summary.dataset.shippingPrice;
        const shippingOutput = summary.querySelector('[data-rwady-shipping-price]');
        if (shippingOutput) {
          shippingOutput.textContent = summary.dataset.shippingFallbackLabel || 'Calculated at checkout';
        }
      });
      console.warn('[RWADY cart] Shopify shipping rate is unavailable.', error);
      summaries.forEach(applyReviewedPrice);
    } finally {
      if (sequence === requestSequence) document.dispatchEvent(new CustomEvent('rwady:shipping-updated'));
    }
  };

  const getPrice = (summary) => {
    const price = reviewedPrices.has(summary) ? reviewedPrices.get(summary) : selectedPrices.get(summary);
    return Number.isFinite(price) && price >= 0 ? price : null;
  };

  const applyReviewedPrice = (summary) => {
    if (!summary || !reviewedPrices.has(summary)) return;
    const price = reviewedPrices.get(summary);
    summary.dataset.shippingPrice = String(price);
    const output = summary.querySelector('[data-rwady-shipping-price]');
    if (output) output.textContent = formatIqd(price);
    const total = summary.querySelector('[data-rwady-order-total]');
    if (total) total.textContent = formatIqd(Number(summary.dataset.cartEffectiveTotalCents || summary.dataset.cartTotalCents) / 100 + price);
  };
  const setReviewedPrice = (summary, price) => {
    if (!summary) return;
    if (typeof price === 'number' && Number.isFinite(price) && price >= 0) {
      reviewedPrices.set(summary, price);
      applyReviewedPrice(summary);
    } else {
      reviewedPrices.delete(summary);
      const original = selectedPrices.get(summary);
      const output = summary.querySelector('[data-rwady-shipping-price]');
      if (Number.isFinite(original)) {
        summary.dataset.shippingPrice = String(original);
        if (output) output.textContent = formatIqd(original);
      } else {
        delete summary.dataset.shippingPrice;
        if (output) output.textContent = summary.dataset.shippingFallbackLabel || 'Calculated at checkout';
      }
    }
  };

  window.RwadyCartShipping = { update, getPrice, setReviewedPrice };
  document.addEventListener('DOMContentLoaded', update, { once: true });
  document.addEventListener(UPDATE_EVENT, () => window.setTimeout(update, 0));
})();

